"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SyncTerminalParamsResponse = exports.TerminalParamRecord = exports.TerminalParamExportDocument = exports.TerminalParamExportRow = exports.TERMINAL_PARAM_EXPORT_FORMAT = exports.TerminalParamAssignment = exports.TerminalParamRegistry = exports.TerminalShiftBoundaryParamState = exports.TERMINAL_PARAM_SECRET_MAX_LENGTH = exports.TERMINAL_PARAM_SECRET_MIN_LENGTH = exports.TERMINAL_PARAM_SECRET_HASH_FORMAT = exports.TERMINAL_PARAM_SECRET_REDACTED = exports.TerminalParamDescriptor = exports.TerminalParamValue = exports.PercentString = exports.TimeOfDayString = exports.TERMINAL_PARAM_GLOBAL_SCOPE_ID = exports.TERMINAL_PARAM_SCOPE_PRECEDENCE = exports.TerminalParamScope = exports.TerminalParamValueType = exports.TerminalParamKey = void 0;
exports.terminalParamValueMatchesType = terminalParamValueMatchesType;
exports.terminalParamSecretViolation = terminalParamSecretViolation;
exports.isAbsoluteStationPath = isAbsoluteStationPath;
exports.terminalParamIsActive = terminalParamIsActive;
exports.resolveTerminalParams = resolveTerminalParams;
const zod_1 = require("zod");
const pricing_js_1 = require("./pricing.js");
/**
 * Terminal operator parameters (YUKI-41).
 *
 * BOS-authored scalar settings that change how a till behaves —
 * "round cash to 1 UAH", "cap bonus redemption at 50% of the receipt",
 * "auto-close the fiscal day at 23:50". Deliberately a separate domain from
 * `TerminalConfig` in `sync.ts`, which carries *structural* configuration the
 * terminal needs in order to function at all (which fiscal binding, which tax
 * groups). Parameters are policy, not structure: an unrecognised one is
 * survivable, a missing fiscal binding is not.
 *
 * ## Why this is data and not a schema of named fields
 *
 * The obvious modelling — one zod field per parameter — was rejected. It makes
 * every new parameter a contract change, a codegen run, a Dart hand-port and a
 * coordinated release across two repositories, for what is in substance a row
 * in a settings table. Roughly twenty parameters arrive with the first customer
 * programme alone and the rate does not slow down.
 *
 * So the wire carries `(key, type, value)` triples and the *registry* of what
 * keys exist is itself data ([TerminalParamDescriptor]). Adding a parameter is
 * a registry entry plus the code that reads it — no wire change.
 *
 * The cost of that choice is that the wire is not self-validating: nothing in
 * the type system stops `payment.maxCashPerReceipt` arriving as `true`. That is
 * paid for by [terminalParamValueMatchesType], which every producer and
 * consumer runs, and by the terminal treating a type mismatch the same way it
 * treats an unknown key — the parameter is dropped and its baked-in default
 * stands. A mistyped parameter must never be able to take a till down.
 *
 * ## Defaults live on the terminal, not here
 *
 * [TerminalParamDescriptor.defaultValue] drives the BOS authoring UI: it is
 * what the operator sees pre-filled and what "reset to default" restores. It is
 * **not** what an unsynced terminal uses. A till that has never reached BOS —
 * factory-fresh, or newly paired in a shop with no connectivity — must still
 * open and sell correctly, so every parameter's real default is compiled into
 * the terminal. The two must agree, and the terminal side carries a test that
 * makes drift visible.
 *
 * ## Resolution
 *
 * Parameters are authored at three scopes and resolved most-specific-wins:
 * estate-wide → terminal profile → terminal. BOS resolves this server-side and
 * the terminal receives a flat, already-resolved set;
 * [TerminalParamRecord.sourceScope] is carried purely so support can answer
 * "why is this till doing that?" without reading three tables.
 *
 * Note what the widest scope is *not*. Under ADR-0011 each tenant owns its own
 * database and business tables carry no `tenantId`, so there is nothing for a
 * "tenant" scope to key on — the database already *is* the tenant. The widest
 * scope is therefore `global`, meaning "every terminal in this estate", and the
 * ladder stays a genuine three rungs rather than a tenant discriminator
 * smuggled into a business table.
 */
// ---------- Primitive shapes ----------
/**
 * Dotted, namespaced key: a lowercase namespace then one or more lowerCamelCase
 * segments — `payment.maxCashPerReceipt`, `shift.autoZ.enabled`.
 *
 * The namespace exists so the BOS editor can group parameters without a second
 * field to keep in sync, and so a reader can tell at a glance which subsystem
 * owns a setting.
 */
exports.TerminalParamKey = zod_1.z
    .string()
    .min(3)
    .max(96)
    .regex(/^(?:[a-z][a-z0-9]*(?:\.[a-z][a-zA-Z0-9]*)+|snx\.sync\.sanitex\.[a-z][a-z0-9_]*\.maxAgeSeconds)$/, 'a lowercase namespace followed by dot-separated lowerCamelCase segments, e.g. "payment.maxCashPerReceipt"');
/**
 * The value types a parameter may take.
 *
 * Kept small on purpose. Every member here is a type the BOS editor needs a
 * bespoke input for and the terminal needs a bespoke accessor for, so each one
 * has a real cost; anything more structured than these belongs in its own
 * contract rather than in the parameter bag.
 *
 * - `bool`            — a switch.
 * - `int`             — a plain count. Not money, not a duration.
 * - `money`           — integer minor units as a string, per [MoneyMinor].
 *                       Never a float: these values reach fiscal receipts.
 * - `percent`         — a decimal percentage string, `"0"` to `"100"`.
 * - `string`          — free text (a message, a prefix, an item code).
 * - `enum`            — one of [TerminalParamDescriptor.enumValues].
 * - `timeOfDay`       — local wall-clock `HH:mm`, 24-hour. Local because the
 *                       operator means "ten to midnight in the shop", which is
 *                       not a fixed UTC instant across a DST boundary.
 * - `durationSeconds` — a span in whole seconds.
 * - `stringList`      — an ordered list of strings (a reason list, a set of
 *                       card prefixes, the actions requiring confirmation).
 */
exports.TerminalParamValueType = zod_1.z.enum([
    'bool',
    'int',
    'money',
    'percent',
    'string',
    'enum',
    'timeOfDay',
    'durationSeconds',
    'stringList',
]);
/**
 * Where a parameter was authored. Resolution is most-specific-wins.
 *
 * - `global`   — every terminal in the estate. See the file header for why
 *                this is not called `tenant`.
 * - `profile`  — every terminal bound to one terminal profile. The profile is
 *                the grouping the estate already has; introducing a second,
 *                parallel "parameter group" would mean two answers to "which
 *                tills does this affect".
 * - `terminal` — one till.
 */
exports.TerminalParamScope = zod_1.z.enum(['global', 'profile', 'terminal']);
/**
 * Precedence, low to high. Exported so BOS and any reconciliation tooling sort
 * by the same rule rather than each reimplementing it.
 */
exports.TERMINAL_PARAM_SCOPE_PRECEDENCE = Object.freeze({ global: 0, profile: 1, terminal: 2 });
/**
 * The `scopeId` a `global` assignment carries.
 *
 * `global` has nothing to point at, but the storage layer's uniqueness is
 * `(key, scope, scopeId)` and Postgres does not treat NULLs as equal — a
 * nullable column would let the same global parameter be authored twice with
 * no constraint violation. A sentinel keeps one row per key and keeps the
 * constraint doing its job.
 */
exports.TERMINAL_PARAM_GLOBAL_SCOPE_ID = '*';
/** `HH:mm`, 24-hour, leading zeros required. */
exports.TimeOfDayString = zod_1.z
    .string()
    .regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/, 'a 24-hour local wall-clock time, "HH:mm"');
/** `"0"` … `"100"`, at most two decimal places. */
exports.PercentString = zod_1.z
    .string()
    .regex(/^(?:\d{1,2}(?:\.\d{1,2})?|100(?:\.0{1,2})?)$/, 'a decimal percentage from "0" to "100", with at most 2 decimal places');
/**
 * The value carried on the wire.
 *
 * A union rather than a discriminated union: the discriminator is the sibling
 * `type` field, because the same value must round-trip through the BOS settings
 * table, the JSON wire and the Dart accessor without being re-wrapped at each
 * boundary. [terminalParamValueMatchesType] is what makes the pairing safe.
 */
exports.TerminalParamValue = zod_1.z.union([
    zod_1.z.boolean(),
    zod_1.z.number().int(),
    zod_1.z.string(),
    zod_1.z.array(zod_1.z.string().max(512)).max(200),
]);
/**
 * Whether `value` is well-formed for `type`.
 *
 * The single source of truth for parameter validity, used by the BOS editor
 * before it saves, by the sync endpoint before it emits, and by the contract
 * tests. The terminal has a hand-ported twin.
 *
 * `enumValues` is only consulted for `type === 'enum'`; passing it for any
 * other type is harmless.
 */
function terminalParamValueMatchesType(type, value, enumValues) {
    switch (type) {
        case 'bool':
            return typeof value === 'boolean';
        case 'int':
            return typeof value === 'number' && Number.isInteger(value);
        case 'durationSeconds':
            return typeof value === 'number' && Number.isInteger(value) && value >= 0;
        case 'money':
            return typeof value === 'string' && pricing_js_1.MoneyMinor.safeParse(value).success;
        case 'percent':
            return typeof value === 'string' && exports.PercentString.safeParse(value).success;
        case 'timeOfDay':
            return typeof value === 'string' && exports.TimeOfDayString.safeParse(value).success;
        case 'string':
            return typeof value === 'string';
        case 'enum':
            return typeof value === 'string' && (enumValues === undefined || enumValues.includes(value));
        case 'stringList':
            return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
    }
}
// ---------- Registry (authoring side) ----------
/**
 * What a parameter *is*: its type, its default, and enough presentation
 * metadata for BOS to render an editor without a per-parameter UI change.
 *
 * Lives in BOS. Terminals never receive descriptors — they receive resolved
 * values and hold their own compiled defaults.
 */
exports.TerminalParamDescriptor = zod_1.z
    .object({
    key: exports.TerminalParamKey,
    type: exports.TerminalParamValueType,
    /**
     * What BOS pre-fills and what "reset to default" restores. Must match the
     * terminal's compiled default for the same key; see the file header.
     */
    defaultValue: exports.TerminalParamValue,
    /** Editor label. Operator-facing, so translatable in BOS. */
    label: zod_1.z.string().min(1).max(160),
    /** One or two sentences on what the parameter does and when to change it. */
    description: zod_1.z.string().max(2000).optional(),
    /** Editor grouping. Defaults to the key's namespace when absent. */
    group: zod_1.z.string().min(1).max(64).optional(),
    /** Permitted values — required for, and only meaningful for, `enum`. */
    enumValues: zod_1.z.array(zod_1.z.string().min(1).max(64)).min(1).max(64).optional(),
    /**
     * Catalogue-backed string/stringList choices; values remain stable IDs.
     *
     * This is the sanctioned way to give a key a richer editor. The field
     * renderer has no per-key branches and must never grow one, so a
     * parameter whose value is picked from a catalogue names the catalogue
     * here instead — the cost is paid once, in the contract, rather than as a
     * UI ticket per key.
     *
     * `terminalAudio` (RL-416) lists the WAVs an operator has uploaded; the
     * stored value is the file's sha-256, which is what the terminal fetches
     * by.
     */
    optionsSource: zod_1.z.enum(['uiThemes', 'terminalAudio']).optional(),
    /**
     * The catalogue is the preferred source but not the only legal one.
     *
     * Set while a consumer is migrating onto a catalogue: the editor offers
     * the list AND accepts a value outside it, and the write-side check
     * relaxes to match. Without this a catalogue lands as a cliff — the moment
     * the picker appears, every value the old mechanism used becomes
     * unauthorable, even though the thing reading it has not changed yet.
     *
     * The value names the syntax accepted outside the catalogue. A boolean is
     * deliberately insufficient: without the syntax, a missing/deleted
     * catalogue id is indistinguishable from a legitimate custom value.
     *
     * `terminalAudio` carries `absolutePath` because terminals still resolve
     * `kitchen.orders.newOrderSoundFile` as a filesystem path (RL-418). Clear
     * it only after the hash-capable station release is installed across the
     * fleet and path assignments have been migrated.
     */
    optionsCustomValue: zod_1.z.enum(['absolutePath']).optional(),
    /**
     * The optional feature module that owns this parameter.
     *
     * Absent means always-on: every deployment declares the key. Present means
     * the key only exists where that module is both wired into the image and
     * entitled for the tenant — BOS filters it out of the registry, refuses
     * writes to it, and stops pushing it to terminals whose handshake dropped
     * the module's sync domain. Without this, an optional module's parameters
     * render as editable rows whose catalogue endpoint answers 404.
     */
    module: zod_1.z.string().min(1).max(64).optional(),
    /** Inclusive bounds for `int` / `durationSeconds`. */
    minInt: zod_1.z.number().int().optional(),
    maxInt: zod_1.z.number().int().optional(),
    /** Inclusive minor-unit bounds for `money`, encoded like the value itself. */
    minMoney: pricing_js_1.MoneyMinor.optional(),
    maxMoney: pricing_js_1.MoneyMinor.optional(),
    /**
     * Scopes at which this parameter may be authored. A parameter tied to
     * physical hardware belongs at `terminal`; a trading rule usually belongs
     * at `global`. Defaults to all three.
     */
    scopes: zod_1.z.array(exports.TerminalParamScope).min(1).default(['global', 'profile', 'terminal']),
    /**
     * Retired keys stay in the registry so BOS can explain a value still held
     * in the settings table instead of rendering it as an unknown row.
     */
    deprecated: zod_1.z.boolean().default(false),
    /**
     * Whether an operator may author this parameter (TP-FR-014).
     *
     * `false` marks a machine-owned value: one a projection or integration
     * writes and would silently restore if an operator overwrote it. BOS shows
     * such a parameter read-only, never hides it, and refuses authoring writes
     * at the API rather than only in the UI. `returns.reasons` is the case —
     * a tree projected from GP Universal API `SALESDOCS`.
     */
    authorable: zod_1.z.boolean().default(true),
    /**
     * Where a machine-owned value comes from, in operator-facing words. Shown
     * beside the read-only value so someone debugging an empty list can see
     * that it exists and who fills it. Meaningful only when `authorable` is
     * `false`.
     */
    provenance: zod_1.z.string().min(1).max(300).optional(),
    /**
     * When a delivered change takes effect on the till (RL-267).
     *
     * Absent means `nextPoll`: the till applies the value on its next
     * parameter poll, roughly 30 seconds later, open shift or not
     * (TP-FR-020). `shiftBoundary` marks the few keys whose mid-shift
     * application has a fiscal consequence — the till holds a changed value
     * while a shift is open and applies it once the shift is closed (Z).
     * `fiscal.tradingEntity` is the case: a sale under one trading entity and
     * its return under the other refund at the wrong rate.
     *
     * Authoring-side metadata, deliberately **not** an activation-policy
     * framework (TP-NG-001): the till holds these keys by its own compiled
     * list (`PosParams.heldUntilZ`), exactly as it holds its own defaults, and
     * this flag lets BOS state the timing truthfully. The two must agree.
     */
    appliesAt: zod_1.z.enum(['nextPoll', 'shiftBoundary']).optional(),
    /**
     * What a till does when the key is set at no scope (RL-423).
     *
     * Absent means `default`: the till falls back to its compiled default,
     * which equals [defaultValue], so the editor may show that value as the
     * one in effect. `keepCurrent` marks a key that drives a setting the
     * device itself holds — the till acts only on an authored value and, with
     * none, leaves the device as it is. Clearing such a key therefore never
     * switches anything back; the operator authors the value they want
     * instead. The editor must not present [defaultValue] as the value in
     * effect for these keys: `fiscal.prro.testMode` is the case, where a
     * register put into test mode stays in test mode until `false` is
     * authored.
     *
     * Authoring-side metadata, never on the wire: [defaultValue] stays the
     * type-valid value the terminal compiles in, and the drift test still
     * compares the two.
     */
    whenUnset: zod_1.z.enum(['default', 'keepCurrent']).optional(),
    /**
     * The value is a secret the operator types and BOS never stores (MICRO-10).
     *
     * `passwordHash`: the operator enters a plain password on a dedicated
     * write endpoint; BOS stores, and the till receives, only a salted Argon2id
     * hash of it (see [TERMINAL_PARAM_SECRET_HASH_FORMAT]). Every admin read —
     * list, resolved view, history, export, import/copy preview — shows
     * [TERMINAL_PARAM_SECRET_REDACTED] in place of the stored hash, so "is a
     * password set here" is answerable and the hash itself is not. The
     * generic write endpoint, import and copy refuse the key: a hash typed or
     * carried in a file is not a password anybody chose.
     *
     * Requires `type: 'string'` with an empty default: empty is "no password
     * set", which a till must treat as "locked", never as "open".
     */
    secret: zod_1.z.enum(['passwordHash']).optional(),
    /**
     * MICRO-46: a secret that needs a longer password than
     * [TERMINAL_PARAM_SECRET_MIN_LENGTH] (up to the maximum). Only on a secret.
     */
    secretMinLength: zod_1.z.number().int().optional(),
    /**
     * MICRO-46: the secrets this one must never equal. BOS refuses a password
     * that any stored value of these keys was made from, at any scope. Only on
     * a secret; declare it on both sides.
     */
    secretDistinctFrom: zod_1.z.array(exports.TerminalParamKey).min(1).max(4).optional(),
    /**
     * This parameter only has an effect while another enum parameter holds
     * one of [values] (VL-365).
     *
     * `payment.bankTerminals.ratio` is the case: the PrivatBank : Oschadbank
     * ratio is read only in automatic terminal selection. The editor shows the
     * parameter disabled, saying why, wherever the controlling key's value in
     * effect is outside [values], and a hand edit there is refused. The till
     * ignores the value in the same case by its own code, so this is
     * authoring-side metadata only and never reaches the wire.
     *
     * Deliberately one level and enum-only: a dependency on a dependency, or
     * on a free-text value, is a rule the editor cannot explain in one line.
     */
    activeWhen: zod_1.z
        .object({
        key: exports.TerminalParamKey,
        values: zod_1.z.array(zod_1.z.string().min(1).max(64)).min(1).max(64),
    })
        .strict()
        .optional(),
})
    .strict()
    .superRefine((descriptor, ctx) => {
    if (descriptor.secret !== undefined &&
        (descriptor.type !== 'string' ||
            descriptor.defaultValue !== '' ||
            descriptor.optionsSource !== undefined)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['secret'],
            message: 'secret requires type "string", an empty defaultValue and no optionsSource',
        });
    }
    if (descriptor.secret === undefined &&
        (descriptor.secretMinLength !== undefined || descriptor.secretDistinctFrom !== undefined)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['secretMinLength'],
            message: 'secretMinLength and secretDistinctFrom require secret',
        });
    }
    if (descriptor.secretMinLength !== undefined &&
        (descriptor.secretMinLength < exports.TERMINAL_PARAM_SECRET_MIN_LENGTH ||
            descriptor.secretMinLength > exports.TERMINAL_PARAM_SECRET_MAX_LENGTH)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['secretMinLength'],
            message: `secretMinLength must be ${exports.TERMINAL_PARAM_SECRET_MIN_LENGTH}..${exports.TERMINAL_PARAM_SECRET_MAX_LENGTH}`,
        });
    }
    // The editor can show "not set" in place of a value only for a switch so
    // far; widening this needs that rendering for the other types first.
    if (descriptor.whenUnset === 'keepCurrent' && descriptor.type !== 'bool') {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['whenUnset'],
            message: 'whenUnset "keepCurrent" is only supported for type "bool"',
        });
    }
    if (descriptor.optionsSource && !['string', 'stringList'].includes(descriptor.type)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['optionsSource'],
            message: 'optionsSource requires string or stringList',
        });
    }
    if (descriptor.authorable && descriptor.provenance !== undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['provenance'],
            message: 'provenance is only meaningful when authorable is false',
        });
    }
    if (!descriptor.authorable && descriptor.provenance === undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['provenance'],
            message: 'a not-authorable parameter must state its provenance',
        });
    }
    if (descriptor.type === 'enum' && descriptor.enumValues === undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['enumValues'],
            message: 'enumValues is required when type is "enum"',
        });
    }
    if (descriptor.type !== 'enum' && descriptor.enumValues !== undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['enumValues'],
            message: 'enumValues is only meaningful when type is "enum"',
        });
    }
    if (descriptor.enumValues !== undefined &&
        new Set(descriptor.enumValues).size !== descriptor.enumValues.length) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['enumValues'],
            message: 'enumValues must not contain duplicates',
        });
    }
    if (new Set(descriptor.scopes).size !== descriptor.scopes.length) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['scopes'],
            message: 'scopes must not contain duplicates',
        });
    }
    if (descriptor.type !== 'int' &&
        descriptor.type !== 'durationSeconds' &&
        (descriptor.minInt !== undefined || descriptor.maxInt !== undefined)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['minInt'],
            message: 'integer bounds require type "int" or "durationSeconds"',
        });
    }
    if (descriptor.type !== 'money' &&
        (descriptor.minMoney !== undefined || descriptor.maxMoney !== undefined)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['minMoney'],
            message: 'money bounds require type "money"',
        });
    }
    if (!terminalParamValueMatchesType(descriptor.type, descriptor.defaultValue, descriptor.enumValues)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['defaultValue'],
            message: `defaultValue is not a valid ${descriptor.type}`,
        });
    }
    if (descriptor.minInt !== undefined &&
        descriptor.maxInt !== undefined &&
        descriptor.minInt > descriptor.maxInt) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['maxInt'],
            message: 'maxInt must be greater than or equal to minInt',
        });
    }
    if (typeof descriptor.defaultValue === 'number' &&
        descriptor.minInt !== undefined &&
        descriptor.defaultValue < descriptor.minInt) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['defaultValue'],
            message: 'defaultValue must be greater than or equal to minInt',
        });
    }
    if (typeof descriptor.defaultValue === 'number' &&
        descriptor.maxInt !== undefined &&
        descriptor.defaultValue > descriptor.maxInt) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['defaultValue'],
            message: 'defaultValue must be less than or equal to maxInt',
        });
    }
    if (descriptor.minMoney !== undefined &&
        descriptor.maxMoney !== undefined &&
        BigInt(descriptor.minMoney) > BigInt(descriptor.maxMoney)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['maxMoney'],
            message: 'maxMoney must be greater than or equal to minMoney',
        });
    }
    if (descriptor.type === 'money' &&
        typeof descriptor.defaultValue === 'string' &&
        pricing_js_1.MoneyMinor.safeParse(descriptor.defaultValue).success) {
        const defaultMoney = BigInt(descriptor.defaultValue);
        if (descriptor.minMoney !== undefined && defaultMoney < BigInt(descriptor.minMoney)) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['defaultValue'],
                message: 'defaultValue must be greater than or equal to minMoney',
            });
        }
        if (descriptor.maxMoney !== undefined && defaultMoney > BigInt(descriptor.maxMoney)) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['defaultValue'],
                message: 'defaultValue must be less than or equal to maxMoney',
            });
        }
    }
});
// ---------- Secret parameters (MICRO-10) ----------
/**
 * What every admin surface shows in place of a secret parameter's stored hash.
 *
 * Non-empty, so the editor's generic "is something set" logic keeps working,
 * and unusable as a password (no digit), so a client that echoes it back
 * cannot set it.
 */
exports.TERMINAL_PARAM_SECRET_REDACTED = '********';
/**
 * The stored and delivered form of a `secret: 'passwordHash'` value: the PHC
 * string Argon2id writes, with the cashier-PIN cost parameters and no pepper.
 *
 *     $argon2id$v=19$m=65536,t=3,p=4$<salt>$<hash>
 *
 * `<salt>` is 16 random bytes, `<hash>` 32 bytes, both standard base64 without
 * `=` padding. The hashed input is the UTF-8 bytes of the password exactly as
 * typed — no trimming, no normalisation, no pepper. A till verifies with the
 * same Argon2id it uses for cashier PINs, reading the parameters from the
 * string.
 */
exports.TERMINAL_PARAM_SECRET_HASH_FORMAT = /^\$argon2id\$v=19\$m=65536,t=3,p=4\$[A-Za-z0-9+/]{22}\$[A-Za-z0-9+/]{43}$/;
/** Bounds of a secret password, in characters. */
exports.TERMINAL_PARAM_SECRET_MIN_LENGTH = 8;
exports.TERMINAL_PARAM_SECRET_MAX_LENGTH = 64;
/**
 * Whether `password` may become a service password, or why not.
 *
 * Printable ASCII only (0x20–0x7E, no leading or trailing space): the password
 * is typed on whatever keyboard the lane offers, and a character one layout
 * cannot produce, or one that two keyboards encode differently, is a lane
 * nobody can open. At least one letter and one digit, and not one character
 * repeated, so the trivially guessed values are out. One rule for the editor
 * and the API.
 */
function terminalParamSecretViolation(password, 
/** MICRO-46: the descriptor's `secretMinLength`, when it asks for more. */
minLength = exports.TERMINAL_PARAM_SECRET_MIN_LENGTH) {
    if (password.length < Math.max(minLength, exports.TERMINAL_PARAM_SECRET_MIN_LENGTH))
        return 'tooShort';
    if (password.length > exports.TERMINAL_PARAM_SECRET_MAX_LENGTH)
        return 'tooLong';
    if (!/^[\x20-\x7E]+$/.test(password) || password.trim() !== password)
        return 'charset';
    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password))
        return 'needsLetterAndDigit';
    if (new Set(password).size < 3)
        return 'repeated';
    return null;
}
/**
 * What a till reports for one shift-boundary parameter (RL-267), inside
 * telemetry `health.shiftBoundaryParams`: the value it is operating under
 * now and, while a shift is open, the delivered value it is holding for the
 * next shift boundary. `pendingValue` is null when nothing is held.
 *
 * This makes the deferred state visible in BOS rather than inferred from
 * "authored ≠ reported". BOS is a mirror: it never acts on it.
 */
exports.TerminalShiftBoundaryParamState = zod_1.z.object({
    key: exports.TerminalParamKey,
    value: exports.TerminalParamValue,
    pendingValue: exports.TerminalParamValue.nullable(),
});
/**
 * A station-local absolute path accepted during catalogue migrations.
 *
 * It is intentionally a shape check: BOS cannot inspect another machine's
 * filesystem. Control characters are refused so a value cannot corrupt logs
 * or turn into an invalid process argument. POSIX, drive-letter Windows and
 * UNC paths are all absolute forms used by supported stations.
 */
function isAbsoluteStationPath(value) {
    if (value.length === 0 || value.length > 4096 || value.trim() !== value)
        return false;
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index);
        if (code <= 0x1f || code === 0x7f)
            return false;
    }
    return (/^\//.test(value) || /^[A-Za-z]:[\\/]/.test(value) || /^\\\\[^\\/]+[\\/][^\\/]+/.test(value));
}
/**
 * The full set of parameters a deployment knows about.
 *
 * `version` bumps whenever the descriptor set changes, so BOS can cache the
 * editor schema and terminals' support tooling can report which registry a
 * value was authored against.
 */
exports.TerminalParamRegistry = zod_1.z
    .object({
    version: zod_1.z.number().int().nonnegative(),
    descriptors: zod_1.z.array(exports.TerminalParamDescriptor).max(1000),
})
    .strict()
    .superRefine((registry, ctx) => {
    const seen = new Set();
    for (const [index, descriptor] of registry.descriptors.entries()) {
        if (seen.has(descriptor.key)) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['descriptors', index, 'key'],
                message: `duplicate parameter key "${descriptor.key}"`,
            });
        }
        seen.add(descriptor.key);
    }
    // VL-365: `activeWhen` must name another enum parameter of the same
    // registry, by values that parameter can take, and must not chain.
    const byKey = new Map(registry.descriptors.map((d) => [d.key, d]));
    for (const [index, descriptor] of registry.descriptors.entries()) {
        const rule = descriptor.activeWhen;
        if (rule === undefined)
            continue;
        const path = ['descriptors', index, 'activeWhen'];
        const controller = byKey.get(rule.key);
        if (controller === undefined || controller.key === descriptor.key) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path,
                message: `activeWhen names no other parameter ("${rule.key}")`,
            });
            continue;
        }
        if (controller.type !== 'enum') {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path,
                message: `activeWhen must name an enum parameter ("${rule.key}" is ${controller.type})`,
            });
            continue;
        }
        if (controller.activeWhen !== undefined) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path,
                message: `activeWhen must not chain ("${rule.key}" has its own activeWhen)`,
            });
        }
        const allowed = new Set(controller.enumValues ?? []);
        for (const value of rule.values) {
            if (!allowed.has(value)) {
                ctx.addIssue({
                    code: zod_1.z.ZodIssueCode.custom,
                    path,
                    message: `"${value}" is not a value of "${rule.key}"`,
                });
            }
        }
    }
});
/**
 * Whether [descriptor] has an effect given the value [controllingValue] its
 * `activeWhen` parameter holds where it is being authored (VL-365). Always
 * true for a descriptor without `activeWhen`.
 */
function terminalParamIsActive(descriptor, controllingValue) {
    const rule = descriptor.activeWhen;
    if (rule === undefined)
        return true;
    return typeof controllingValue === 'string' && rule.values.includes(controllingValue);
}
// ---------- Authored values (storage side) ----------
/**
 * One authored value at one scope. This is what the BOS settings table holds;
 * `scopeId` is the profile or terminal id the row is attached to, or
 * [TERMINAL_PARAM_GLOBAL_SCOPE_ID] for an estate-wide value.
 */
exports.TerminalParamAssignment = zod_1.z
    .object({
    key: exports.TerminalParamKey,
    type: exports.TerminalParamValueType,
    value: exports.TerminalParamValue,
    scope: exports.TerminalParamScope,
    scopeId: zod_1.z.string().min(1).max(64),
})
    .strict()
    .superRefine((assignment, ctx) => {
    if (!terminalParamValueMatchesType(assignment.type, assignment.value)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['value'],
            message: `value is not a valid ${assignment.type}`,
        });
    }
});
// ---------- Export / import document (TP-FR-028, TP-FR-029) ----------
/** The `format` stamp every exported parameter file carries. */
exports.TERMINAL_PARAM_EXPORT_FORMAT = 'reform-lite.terminal-params';
/**
 * One authored assignment as it travels in an export file. `scopeName` is
 * the unique profile name or terminal code. A same-tenant import resolves by
 * `scopeId`; a cross-tenant import uses this identity because database ids
 * are tenant-local.
 */
exports.TerminalParamExportRow = zod_1.z
    .object({
    key: exports.TerminalParamKey,
    type: exports.TerminalParamValueType,
    value: exports.TerminalParamValue,
    scope: exports.TerminalParamScope,
    scopeId: zod_1.z.string().min(1).max(64),
    scopeName: zod_1.z.string().min(1).max(200).optional(),
})
    .strict();
/**
 * A tenant's authored parameter assignments as a self-describing JSON file.
 *
 * JSON rather than CSV: parameters are typed and sometimes nested — a
 * `stringList`, the return-reason tree — and CSV would flatten them into
 * encoding conventions. Stamped with the registry version and the tenant of
 * origin so an importer can say where a file came from and re-validate every
 * key against its own registry (TP-FR-031). Carries authored values only,
 * never resolved defaults.
 */
exports.TerminalParamExportDocument = zod_1.z
    .object({
    format: zod_1.z.literal(exports.TERMINAL_PARAM_EXPORT_FORMAT),
    formatVersion: zod_1.z.literal(1),
    registryVersion: zod_1.z.number().int().nonnegative(),
    tenant: zod_1.z.object({ id: zod_1.z.string().min(1).max(64), slug: zod_1.z.string().min(1).max(64) }).strict(),
    exportedAt: zod_1.z.string().datetime(),
    assignments: zod_1.z.array(exports.TerminalParamExportRow).max(5000),
})
    .strict()
    .superRefine((document, ctx) => {
    const seen = new Set();
    document.assignments.forEach((row, index) => {
        const scopeId = row.scope === 'global' ? exports.TERMINAL_PARAM_GLOBAL_SCOPE_ID : row.scopeId;
        const address = `${row.key}|${row.scope}|${scopeId}`;
        if (seen.has(address)) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['assignments', index],
                message: `duplicate assignment for ${row.key} at ${row.scope} ${scopeId}`,
            });
        }
        seen.add(address);
    });
});
// ---------- Download — Terminal Params ----------
/**
 * One already-resolved parameter as the terminal receives it.
 *
 * No default, no descriptor, no scope chain — just the winning value and, for
 * support, where it came from.
 */
exports.TerminalParamRecord = zod_1.z
    .object({
    key: exports.TerminalParamKey,
    type: exports.TerminalParamValueType,
    value: exports.TerminalParamValue,
    /** Which scope won resolution. Diagnostic only; the terminal ignores it. */
    sourceScope: exports.TerminalParamScope,
})
    .strict()
    .superRefine((record, ctx) => {
    if (!terminalParamValueMatchesType(record.type, record.value)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['value'],
            message: `value is not a valid ${record.type}`,
        });
    }
});
/**
 * `GET /sync/terminal-params`.
 *
 * A snapshot, never a delta. The resolved set is small — tens of rows — and a
 * partially-applied parameter set is a genuinely dangerous state: a till that
 * had picked up "cap bonus redemption at 50%" but not yet "bonuses are a
 * discount, not a tender" would mis-fiscalise. Sending the whole set every time
 * means the terminal swaps one coherent set for another.
 *
 * `params` is therefore exhaustive: a key absent from it is a key the terminal
 * must fall back to its compiled default for, including when it previously had
 * a value. That is how "operator cleared the override" is expressed, and it is
 * why this must not be modelled as a merge.
 */
exports.SyncTerminalParamsResponse = zod_1.z
    .object({
    params: zod_1.z.array(exports.TerminalParamRecord).max(1000),
    /** Registry version the values were resolved against. Diagnostic. */
    registryVersion: zod_1.z.number().int().nonnegative(),
    /** Cursor opaque — the terminal calls back with this string. */
    cursor: zod_1.z.string().min(1).max(2048),
    /** Always false — this is a single-shot snapshot, never paged. */
    hasMore: zod_1.z.literal(false),
    snapshotAt: zod_1.z.string().datetime(),
})
    .strict()
    .superRefine((response, ctx) => {
    const seen = new Set();
    for (const [index, record] of response.params.entries()) {
        if (seen.has(record.key)) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['params', index, 'key'],
                message: `duplicate parameter key "${record.key}"`,
            });
        }
        seen.add(record.key);
    }
});
// ---------- Resolution ----------
/**
 * Collapses authored assignments into the flat set a terminal receives.
 *
 * `scopeIds` names the scopes this terminal actually belongs to — the estate
 * sentinel, its profile, itself. Assignments attached to any other scope id are
 * ignored, so the caller may pass a broad query result without pre-filtering.
 *
 * Ties within a scope cannot occur: the settings table is unique on
 * `(key, scope, scopeId)`. Across scopes the more specific wins, and an
 * assignment whose value does not match its own declared type is dropped —
 * shipping a malformed value to a till is strictly worse than shipping the
 * terminal's default.
 */
function resolveTerminalParams(assignments, scopeIds) {
    const winners = new Map();
    for (const assignment of assignments) {
        if (scopeIds[assignment.scope] !== assignment.scopeId)
            continue;
        if (!terminalParamValueMatchesType(assignment.type, assignment.value))
            continue;
        const incumbent = winners.get(assignment.key);
        if (incumbent === undefined ||
            exports.TERMINAL_PARAM_SCOPE_PRECEDENCE[assignment.scope] >
                exports.TERMINAL_PARAM_SCOPE_PRECEDENCE[incumbent.scope]) {
            winners.set(assignment.key, assignment);
        }
    }
    return [...winners.values()]
        .map((assignment) => ({
        key: assignment.key,
        type: assignment.type,
        value: assignment.value,
        sourceScope: assignment.scope,
    }))
        .sort((left, right) => left.key.localeCompare(right.key));
}
//# sourceMappingURL=terminal-params.js.map